import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@store/theme/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
}

interface Props extends WidgetProps, HasCategoryContext {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.backgroundColor]: string;
    };
}

export interface CategoryBannerStyles {
    overlayWrapperStyles?: InjectableCss;
    categoryName?: TypographyProps;
}

export const bannerStyles: CategoryBannerStyles = {
    overlayWrapperStyles: {
        css: css`
            width: 100%;
            text-align: center;
            padding: 45px;
            margin-top: 25px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                        text-align: left;
                    `,
                    css`
                        padding: 0 30px;
                        text-align: left;
                    `,
                ])}
        `,
    },
    categoryName: {
        size: 28,
        weight: 700,
        css: css`
            color: ${COLORS.secondary.contrast};
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: [null, `${COLORS.primary.contrast}`], properties: ["color"] }],
                    mediaOptions.max,
                )}
        `,
    },
};

const styles = bannerStyles;

const CategoryBanner: React.FC<Props> = ({ category, fields }: Props) => {
    if (!category || !category.name) {
        return null;
    }

    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const wrapperStyles = {
        css: css`
            ${backgroundStyles}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            background: none;
                        `,
                    ],
                    "max",
                )}
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <StyledWrapper {...styles.overlayWrapperStyles}>
                <Typography {...styles.categoryName}>{category.name}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(CategoryBanner),
    definition: {
        group: "Categories",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
        ],
    },
};

export default widgetModule;
